/**
 * admin-class-completions.ts - ClassCompletion 관련 Firestore 조회 서비스
 *
 * 경로: users/{ownerUid}/centers/{centerId}/classCompletions/{classId}
 */

import { collection, query, where, getDocs } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";

export interface AdminClassCompletionDocument {
  classId: string;
  journalWritten: boolean;
  journalId?: string;
  isNoShow?: boolean;
  updatedAt: unknown;
}

/**
 * 여러 classId에 대한 ClassCompletion을 배치 조회한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param classIds - 조회할 classId 배열
 * @returns classId → ClassCompletion 맵
 */
export async function getClassCompletionsBatch(
  ownerUid: string,
  centerId: number,
  classIds: string[],
): Promise<Map<string, AdminClassCompletionDocument>> {
  const map = new Map<string, AdminClassCompletionDocument>();
  if (classIds.length === 0) return map;

  // Firestore `in` queries support max 30 items per query
  const chunks: string[][] = [];
  for (let i = 0; i < classIds.length; i += 30) {
    chunks.push(classIds.slice(i, i + 30));
  }

  const col = collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "classCompletions",
  );

  const results = await Promise.all(
    chunks.map((chunk) => getDocs(query(col, where("classId", "in", chunk)))),
  );

  for (const snap of results) {
    for (const d of snap.docs) {
      const data = d.data() as AdminClassCompletionDocument;
      map.set(data.classId, data);
    }
  }

  return map;
}
